/* eslint-disable react/jsx-props-no-spreading */
import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { useForm, Controller } from 'react-hook-form';
import Button from '@mui/material/Button';
import TextField from 'components/global/FormTextField';
import { useDispatch, useSelector } from 'react-redux';
import { forgotPassword } from '../../actions/auths';

function ForgotPassword({ intl }) {
  const dispatch = useDispatch();
  const resetPasswordError = useSelector(
    state => state.auth.resetPasswordError,
  );

  const {
    handleSubmit,
    control,
    formState: { isDirty, isValid, isSubmitting },
  } = useForm({
    mode: 'onChange',
    defaultValues: { email: '' },
  });

  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [second, setSecond] = useState(0);
  const [success, setSuccess] = useState('');
  const timerRef = useRef(null);

  useEffect(() => {
    localStorage.removeItem('token');
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const setTimer = useCallback(() => {
    setSecond(prev => {
      if (prev > 0) {
        timerRef.current = setTimeout(setTimer, 1000);
        return prev - 1;
      }
      return 0;
    });
  }, []);

  const onSubmit = useCallback(
    values => {
      if (second === 0) {
        setLoading(true);
        setSent(false);
        dispatch(
          forgotPassword({ password_reset: values }, response => {
            setLoading(false);
            setSent(true);
            setSecond(60);
            setSuccess(response);
            setTimer();
          }),
        );
      } else {
        setLoading(false);
      }
    },
    [dispatch, second, setTimer],
  );

  return (
    <div className="signin-container">
      <div className="row">
        <div className="col">
          <div className="auth-form">
            <h4>{intl.formatMessage({ defaultMessage: 'Forgot Password' })}</h4>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="email"
                control={control}
                rules={{
                  pattern: {
                    value: /^[À-ÿA-Z0-9._%+-]+@[À-ÿA-Z0-9.-]+\.[A-Z]{2,6}$/i,
                    message: intl.formatMessage({
                      defaultMessage: 'Please enter a valid email',
                    }),
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={intl.formatMessage({ defaultMessage: 'Email' })}
                    disabled={loading || second > 0}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <div />
              <Button
                className="flat-button-green"
                primary
                type="submit"
                disabled={
                  !isValid || !isDirty || isSubmitting || loading || sent
                }
              >
                {intl.formatMessage({
                  defaultMessage: 'Send Password Reset',
                })}
              </Button>
              <div className="forget-pw-message">
                {resetPasswordError && (
                  <div className="server-error">{resetPasswordError}</div>
                )}
                {loading && (
                  <span className="p">
                    {intl.formatMessage({ defaultMessage: 'Sending...' })}
                  </span>
                )}
                {success && <p>{success}</p>}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

ForgotPassword.propTypes = {
  intl: PropTypes.object.isRequired,
};

export default injectIntl(ForgotPassword);
